/**
 * Chaque vente BtoB en un point : date de facture × CA HT.
 *
 * Les ventes aux magasins sont peu nombreuses et irrégulières — une grosse
 * commande de rentrée, puis rien pendant trois semaines. Une courbe les
 * lisserait en une tendance qui n'existe pas ; les points montrent ce qui
 * s'est passé : quand, combien, et si les commandes grossissent.
 *
 * Une seule série, donc une seule couleur et pas de légende. Le magasin est
 * dans l'infobulle et dans la vue tableau.
 */

import type { BtobSale } from '@shared/types/btob';
import { CartesianGrid, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis, ZAxis } from 'recharts';

import { formatDayLong, formatDayShort, formatInteger, formatMoney, formatMoneyCompact } from '../lib/format';
import { AXIS_STYLE, COLORS } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';

interface Point {
  x: number;
  y: number;
  sale: BtobSale;
}

const dayOf = (timestamp: number) => new Date(timestamp).toISOString().slice(0, 10);

function PointTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload?: Point }> }) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  const { sale } = point;
  return (
    <div className="tooltip" role="tooltip">
      <p className="tooltip__title">{sale.storeName}</p>
      <div className="tooltip__row">
        <span className="tooltip__name">{formatDayLong(sale.invoiceDate)}</span>
        <span className="tooltip__value">{formatMoney(sale.revenueHt, 'EUR')} HT</span>
      </div>
      <div className="tooltip__row">
        <span className="tooltip__name">{formatInteger(sale.units)} unités{sale.invoiceNumber ? ` — ${sale.invoiceNumber}` : ''}</span>
        <span className="tooltip__value">{sale.paymentDate ? 'payée' : 'à encaisser'}</span>
      </div>
    </div>
  );
}

const columns: ReadonlyArray<DataColumn<BtobSale>> = [
  { key: 'date', header: 'Facture', render: (row) => formatDayLong(row.invoiceDate) },
  { key: 'store', header: 'Magasin', render: (row) => row.storeName },
  { key: 'units', header: 'Unités', numeric: true, render: (row) => formatInteger(row.units) },
  { key: 'ht', header: 'CA HT', numeric: true, render: (row) => formatMoney(row.revenueHt, 'EUR') },
];

export function BtobScatterChart({ sales }: { sales: readonly BtobSale[] }) {
  const points: Point[] = sales.map((sale) => ({ x: Date.parse(`${sale.invoiceDate}T12:00:00Z`), y: sale.revenueHt, sale }));

  return (
    <ChartCard
      title="Ventes dans le temps"
      subtitle="Une vente par point — date de facture et CA HT, sur douze mois"
      rows={sales}
      columns={columns}
      emptyMessage="Aucune vente BtoB sur ces douze mois."
      chartHeight={300}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            type="number"
            dataKey="x"
            domain={['dataMin - 86400000', 'dataMax + 86400000']}
            scale="time"
            tickFormatter={(value: number) => formatDayShort(dayOf(value))}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
            minTickGap={40}
          />
          <YAxis
            type="number"
            dataKey="y"
            tickFormatter={(value: number) => formatMoneyCompact(value, 'EUR')}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={60}
          />
          {/* Taille fixe, 64 px² ≈ un disque de 9 px : au-dessus du plancher de
              8 px, et assez petit pour que deux ventes proches restent deux. */}
          <ZAxis range={[64, 64]} />
          <Tooltip cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }} content={<PointTooltip />} />
          <Scatter
            data={points}
            isAnimationActive={false}
            fill={COLORS.series1}
            // Anneau couleur surface : deux points qui se chevauchent restent
            // distincts.
            stroke={COLORS.surface}
            strokeWidth={2}
          />
        </ScatterChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
